import { useAiCredits } from '@features/backtesting/hooks/useAiBacktest';
import { BuyCreditsButton } from '../BuyCreditsButton';
import { featureBacktesting } from '@config/env';

/**
 * The pricing page's full-width "Need more credits?" panel (2026-08-16 Figma + user confirmation):
 * the balance chip, one line of copy, and the credit-pack CTA.
 *
 * It is the same purchase as the backtester's row, on purpose — a subscription tops up monthly, a
 * pack tops up now, and the pricing page is exactly where someone comparing plans asks "what if I
 * just need more credits this month?". Answering that here stops the pack being discoverable only
 * once you are already deep in the backtester and out of credits.
 *
 * `emphasis="full"` pins the loud CTA regardless of balance: this panel's entire purpose IS the
 * purchase, so the quiet "Buy more" link the backtester uses at a healthy balance would bury the
 * only action on it. Like `BuyCreditsButton` everywhere, the whole panel renders NOTHING when the
 * pack is unavailable — the button returns null and an empty card would be worse than no card.
 */
export function NeedMoreCreditsBar() {
  // The entire panel is a credit sales pitch, so it follows the Backtesting AI flag (2026-08-17):
  // with the feature off there is nothing to spend credits ON, and offering the purchase anyway
  // takes money for something the buyer cannot open.
  if (!featureBacktesting) return null;
  return <NeedMoreCreditsBarBody />;
}

function NeedMoreCreditsBarBody() {
  const credits = useAiCredits();
  const balance = credits.data?.balance;

  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl bg-canvas-raised sm:flex-row sm:items-center sm:justify-between"
      // Figma "Bot Card": radius 16, padding 24/33, a hairline #ECF1FC border and NO shadow — it is a
      // panel sitting on the page, not a card lifted off it like the three plans above.
      style={{ padding: '24px 33px', border: '1px solid var(--canvas-divider)' }}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <h2 className="text-[18px] font-bold tracking-[0.75px] text-text">Need more credits?</h2>
          <span
            className="shrink-0 whitespace-nowrap rounded-full px-3 text-[10px]"
            style={{ background: 'var(--chip-strong)', color: 'var(--text-muted)', lineHeight: '20px' }}
          >
            Credits <span className="font-semibold">{balance ?? '…'}</span>
          </span>
        </div>
        <p className="text-base text-text-muted">
          {/* Owner-approved 2026-09-25 (NFA AIQ Q17 sweep, H2): the AI translates the rules the
              trader writes; it does not build or strengthen a strategy. */}
          Each credit pays for one AI translation of the rules you write into a backtest. Backtest results are hypothetical.
        </p>
      </div>
      <BuyCreditsButton balance={balance} emphasis="full" />
    </div>
  );
}
